import { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import useSiteText from "../hooks/useSiteText";
import { Mail, MapPin, Clock, Factory, Phone } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useLanguage();
  const text = useSiteText();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    business_type: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t.contact.form.success);
      setFormData({
        name: "",
        company: "",
        business_type: "",
        email: "",
        phone: "",
        message: ""
      });
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error("Error submitting form:", error);
      toast.error(t.contact.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const businessTypes = [
    { value: "wholesale", label: t.contact.form.businessTypes.wholesale },
    { value: "retail", label: t.contact.form.businessTypes.retail },
    { value: "distribution", label: t.contact.form.businessTypes.distribution },
    { value: "restaurant", label: t.contact.form.businessTypes.restaurant },
    { value: "other", label: t.contact.form.businessTypes.other }
  ];

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Header */}
      <section className="py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">{t.nav.contact}</p>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.contact.title}
          </h1>
          <p className="text-xl text-zinc-600 max-w-2xl">{t.contact.subtitle}</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-white" data-testid="b2b-contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Info */}
            <div>
              <h2 className="text-2xl font-bold text-[#0A0A0A] mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {t.contact.subtitle}
              </h2>
              
              <div className="space-y-6">
                {/* Email */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#0A0A0A]" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">{t.contact.info.email}</p>
                    <a href={`mailto:${text("contact.email", "info@tamari.al")}`} className="font-medium text-[#0A0A0A] hover:underline">{text("contact.email", "info@tamari.al")}</a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-4" data-testid="contact-phone">
                  <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#0A0A0A]" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">{t.contact.info.phone}</p>
                    <a href={`tel:${text("contact.phone", "+355 69 624 2511").replace(/\s/g, "")}`} className="font-medium text-[#0A0A0A] hover:underline">{text("contact.phone", "+355 69 624 2511")}</a>
                  </div>
                </div>

                {/* Slaughterhouse */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <Factory className="w-5 h-5 text-[#0A0A0A]" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">{t.contact.info.slaughterhouse}</p>
                    <p className="font-medium text-[#0A0A0A]">{text("contact.slaughterhouse_address", t.contact.info.slaughterhouseAddress)}</p>
                    <p className="text-sm text-zinc-600 flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" /> {text("contact.slaughterhouse_hours", t.contact.info.slaughterhouseHours)}
                    </p>
                  </div>
                </div>

                {/* Salami Factory */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#0A0A0A]" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">{t.contact.info.salami}</p>
                    <p className="font-medium text-[#0A0A0A]">{text("contact.salami_address", t.contact.info.salamiAddress)}</p>
                    <p className="text-sm text-zinc-600 flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" /> {text("contact.salami_hours", t.contact.info.salamiHours)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-zinc-50 p-8 border border-zinc-200">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                      {t.contact.form.name} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full border border-zinc-300 bg-white px-4 py-3 text-[#0A0A0A] focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                      {t.contact.form.company} *
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full border border-zinc-300 bg-white px-4 py-3 text-[#0A0A0A] focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                    {t.contact.form.businessType} *
                  </label>
                  <select
                    name="business_type"
                    value={formData.business_type}
                    onChange={handleChange}
                    required
                    className="w-full border border-zinc-300 bg-white px-4 py-3 text-[#0A0A0A] focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] focus:outline-none"
                  >
                    <option value="">--</option>
                    {businessTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                      {t.contact.form.email}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full border border-zinc-300 bg-white px-4 py-3 text-[#0A0A0A] focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                      {t.contact.form.phone}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full border border-zinc-300 bg-white px-4 py-3 text-[#0A0A0A] focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                    {t.contact.form.message} *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full border border-zinc-300 bg-white px-4 py-3 text-[#0A0A0A] focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] focus:outline-none resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0A0A0A] text-white px-8 py-4 font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-form-submit"
                >
                  {isSubmitting ? "..." : t.contact.form.submit}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
